import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Search, Plus, Calendar, MoreVertical,
    User, ChevronRight, Download, Filter,
    TrendingUp, DollarSign, Clock, AlertCircle,
    X, Building2, ChevronDown, Check, CreditCard,
    FileText, Upload, Trash2, ArrowLeft, Phone, Settings,
    Mail, MessageSquare, MessageCircle, Share2, Printer,
    Eye
} from 'lucide-react';
import { wcfInstance } from '../../axiosinstance';
import API_ENDPOINTS from '../../config/apiEndpoints';
import Loader from '../../components/Loader/Loader';
import Swal from 'sweetalert2';
import { formatCurrency, getInitials, generateColor, isGenericAvatar } from '../../utils/helpers';

// Premium Components
import PremiumModuleWrapper from '../Task/Components/common/PremiumModuleWrapper';
import ScoreCard from '../../components/common/ScoreCard/ScoreCard';
import SubHeader from '../../components/common/SubHeader/SubHeader';
import LeadActionOperation from '../../common/LeadActionOperation/LeadActionOperation';
import Panels from '../../components/common/Panels/Panels';
import { LeadDetails } from '../../components/common/LeadDetails/LeadDetails';
import { MassOperationDrawer } from '../../components/common/MassOperation/MassOperation';
import DynamicCalendar from '../../components/DynamicCalendar/DynamicCalendar';
import usePermissions from '../Master_Settings/usePermissions';
import './RevenueModule.css';

const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-');
};

const RevenueModule = () => {
    const { permissions } = usePermissions('PAG10224');
    const crmOperationPerms = useMemo(() => {
        if (!permissions || !Array.isArray(permissions)) return null;
        return permissions.find(p => p.ModuleCode?.includes('PAG10224')) || {};
    }, [permissions]);

    const [revenueData, setRevenueData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [rowSelection, setRowSelection] = useState({});
    const [viewMode, setViewMode] = useState('list');
    const [activeScorecard, _setActiveScorecard] = useState('All');
    const setActiveScorecard = (key) => {
        if (key === 'ResetAll') {
            setSearchQuery('');
            _setActiveScorecard('All');
        } else {
            _setActiveScorecard(key);
        }
    };
    const [showAddModal, setShowAddModal] = useState(false);
    const [viewingRevenueId, setViewingRevenueId] = useState(null);
    const [revenueDetails, setRevenueDetails] = useState(null);
    const [isDetailsLoading, setIsDetailsLoading] = useState(false);
    const [activeActionMenu, setActiveActionMenu] = useState(null);
    const [leadAction, setLeadAction] = useState({ open: false, mode: 'send-mail', leadId: null, entity: null });
    const [isMassDrawerOpen, setIsMassDrawerOpen] = useState(false);
    const [massOperationMode, setMassOperationMode] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Pagination
    const [pageIndex, setPageIndex] = useState(1);
    const [pageSize, setPageSize] = useState(1000); // Increased for frontend search
    const [totalCount, setTotalCount] = useState(0);
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);

    const [isLeadDetailOpen, setIsLeadDetailOpen] = useState(false);
    const [selectedLeadId, setSelectedLeadId] = useState(null);

    const userId = localStorage.getItem('userId') || '34594';
     const NEWV3_BASE_URL = (process.env.REACT_APP_SERVICES_API_BASE_URL || '').replace(/\/$/, '');
    const LOCAL_BASE_URL = "http://localhost:62194/";
    const NEW_BASE_URL = (process.env.REACT_APP_SERVICES_AZURE_BASEURL || '').replace(/\/$/, '');
    const API_BASE = (process.env.REACT_APP_WCF_API_BASE_URL || '').replace(/\/$/, '');
    const NEW_ADDED_BASE_URL = (process.env.REACT_APP_SERVICES_API_BASE_URL || '').replace(/\/$/, '');
    const GET_REVENUE_LIST = `${NEWV3_BASE_URL}${API_ENDPOINTS.REVENUE.GET_REVENUE_LIST}`.trim();
    const Get_Lead_Customer_Suggestion = `${NEWV3_BASE_URL}${API_ENDPOINTS.REVENUE.GET_REVENUE_LEAD_CUSTOMER_SUGGESTION}`.trim();
    const Get_Currency_List = `${API_BASE}${API_ENDPOINTS.INVOICE.GET_CURRENCY_LIST}`.trim();
    const Get_Invoice_Suggestion = `${NEWV3_BASE_URL}${API_ENDPOINTS.REVENUE.GET_INVOICE_SUGGESTION}`.trim();
    const Get_Revenue_Details = `${NEWV3_BASE_URL}${API_ENDPOINTS.REVENUE.GET_REVENUE_DETAILS}`.trim();
    const Update_Revenue_Status = `${NEWV3_BASE_URL}${API_ENDPOINTS.REVENUE.UPDATE_REVENUE_STATUS}`.trim();
    const Get_Holding_Tax = `${NEWV3_BASE_URL}${API_ENDPOINTS.REVENUE.GET_HOLDING_TAX}`.trim();
    const SAVE_REVENUE = `${NEWV3_BASE_URL}${API_ENDPOINTS.REVENUE.SAVE_REVENUE}`.trim();
    const lastRequestRef = useRef(0);

    const [calendarInitialDate] = useState(new Date());

    const fetchRevenue = async () => {
        const requestId = ++lastRequestRef.current;
        setLoading(true);
        setActiveActionMenu(null);
        try {
            const payload = {
                Token: "-2295521862261168",
                Details: {
                    UserId: parseInt(userId, 10),
                    FilterText: searchQuery || "",
                    OffsetValue: ((pageIndex - 1) * pageSize),
                    PagingSize: pageSize,
                    SearchText: searchQuery || "",
                    StartRow: ((pageIndex - 1) * pageSize),
                    RowCount: pageSize
                }
            };

            const response = await wcfInstance.post(GET_REVENUE_LIST, payload);

            if (requestId !== lastRequestRef.current) return;

            if (response.data && response.data.Status === 1) {
                const items = (response.data.Details || []).map((item, index) => {
                    const name = item.CustomerName || item.customerName || item.PersonName || item.personName || item.EntityName || "";

                    let sortDate = 0;
                    const rawDate = item.RevenueDate || item.revenue_date;
                    if (rawDate && typeof rawDate === 'string' && rawDate.includes('/Date(')) {
                        sortDate = parseInt(rawDate.match(/\d+/)[0], 10);
                    } else if (rawDate) {
                        sortDate = new Date(rawDate).getTime();
                    }

                    return {
                        ...item,
                        CustomerName: name,
                        sortableDate: sortDate,
                        id: item.id || item.Id || item.TransactionId || item.TransactionID || item.RevenueId || item.RevenueID || item.AutoId || item.AutoID || item.idNum || `rev-${(pageIndex - 1) * pageSize + index}`
                    };
                });
                setRevenueData(items);
                setTotalCount(items.length);
            }
        } catch (error) {
            if (requestId === lastRequestRef.current) {
                console.error('Error fetching revenue data:', error);
            }
        } finally {
            if (requestId === lastRequestRef.current) {
                setLoading(false);
            }
        }
    };

    const filteredData = useMemo(() => {
        if (!searchQuery) return revenueData;
        const q = searchQuery.toLowerCase();
        return revenueData.filter(item => {
            const customerName = (item.CustomerName || item.customerName || item.PersonName || "").toLowerCase();
            const description = (item.Description || item.description || "").toLowerCase();
            const reference = (item.Reference || item.reference || "").toLowerCase();
            const amount = String(item.Amount || item.amount || "").toLowerCase();
            const method = (item.PaymentMethod || item.paymentMethod || "").toLowerCase();
            const phone = String(item.MobileNo || item.mobileno || item.Phone || "").toLowerCase();
            
            return customerName.includes(q) || 
                   description.includes(q) || 
                   reference.includes(q) || 
                   amount.includes(q) || 
                   method.includes(q) ||
                   phone.includes(q);
        });
    }, [revenueData, searchQuery]);

    // Date Parser for Calendar
    const parseRevenueDate = (dateVal) => {
        if (!dateVal) return null;
        try {
            if (typeof dateVal === 'string' && dateVal.includes('/Date(')) {
                const ts = parseInt(dateVal.match(/\d+/)[0], 10);
                return new Date(ts);
            }
            const d = new Date(dateVal);
            return isNaN(d.getTime()) ? null : d;
        } catch (e) {
            return null;
        }
    };

    // Calendar Events Mapping
    const calendarEvents = useMemo(() => {
        return filteredData.map(item => {
            const date = parseRevenueDate(item.RevenueDate);
            return {
                id: item.id,
                title: `${item.CustomerName || 'Revenue'} - ${formatCurrency(item.RevenueAmount || item.Amount, 'INR')}`,
                date: date,
                status: 'paid', // Default for now
                extendedProps: { ...item }
            };
        }).filter(ev => ev.date !== null);
    }, [filteredData]);

    // Debounce search query
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Fetch data when pagination changes
    useEffect(() => {
        fetchRevenue();
    }, [pageIndex, pageSize]);

    // Actions logic
    const handleShow = async (id) => {
        setViewingRevenueId(id);
        setIsDetailsLoading(true);

        // Find the actual record to get its raw ID if the passed 'id' is not a number
        let targetId = id;
        if (isNaN(id)) {
            const record = revenueData.find(item => item.id === id);
            if (record) {
                targetId = record.Id || record.id || record.TransactionId || record.RevenueId || record.AutoId;
            }
        }

        try {
            const resp = await wcfInstance.post(Get_Revenue_Details, {
                Token: "-2295521862261168",
                Details: {
                    RevenueId: parseInt(targetId, 10),
                    TimeZone: "India Standard Time",
                    UserId: parseInt(userId, 10)
                }
            });
            if (resp.data && resp.data.Status === 1 && resp.data.Details) {
                setRevenueDetails(resp.data.Details);
            }
        } catch (error) {
            console.error("Error fetching revenue details:", error);
            Swal.fire('Error', 'Failed to load revenue details', 'error');
            setViewingRevenueId(null);
        } finally {
            setIsDetailsLoading(false);
        }
    };

    const handleMarkAsSent = async (transactionId) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "Do you want to mark this revenue as sent?",
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#0366d6',
            cancelButtonColor: '#6a737d',
            confirmButtonText: 'Yes, Mark as Sent',
            cancelButtonText: 'Cancel'
        });

        if (!result.isConfirmed) return;

        try {
            const payload = {
                Token: "-2295521862261168",
                Details: {
                    Mode: "markassent",
                    RevenueIds: transactionId.toString(),
                    TransactionIds: transactionId.toString(),
                    UserId: parseInt(userId, 10)
                }
            };

            const resp = await wcfInstance.post(Update_Revenue_Status, payload);

            if (resp.data && resp.data.Status === 1) {
                Swal.fire({
                    title: 'Success!',
                    text: 'Revenue marked as sent successfully',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false
                });
                fetchRevenue();
                if (viewingRevenueId === transactionId) {
                    handleShow(transactionId);
                }
            } else {
                Swal.fire('Error', resp.data?.Message || 'Failed to update status', 'error');
            }
        } catch (error) {
            console.error("Error marking as sent:", error);
            Swal.fire('Error', 'Failed to update status', 'error');
        }
    };

    const handlePrintRevenue = async (transactionId) => {
        try {
            setLoading(true);
            const rawId = isNaN(transactionId) ? (revenueData.find(item => item.id === transactionId)?.Id || transactionId) : transactionId;
            const payload = {
                Token: "-2295521862261168",
                Details: { RevenueId: parseInt(rawId, 10) }
            };
            const response = await wcfInstance.post(`${NEWV3_BASE_URL}${API_ENDPOINTS.REVENUE.PRINT_REVENUE}`, payload);
            if (response.data && response.data.Status === 1) {
                let url = response.data.Details;
                if (!url.startsWith('http')) {
                    const baseUrl = (process.env.REACT_APP_ASSETS_BASE_URL || 'https://kit19.com').replace(/\/$/, '');
                    const path = url.startsWith('/') ? url : `/${url}`;
                    // Inject UserCRMCampaign if the backend didn't provide the full URL properly
                    url = path.includes('/UserCRMCampaign/') ? `${baseUrl}${path}` : `${baseUrl}/UserCRMCampaign${path}`;
                }
                window.open(url, '_blank');
            } else {
                Swal.fire('Error', response.data?.Message || 'Failed to generate print URL', 'error');
            }
        } catch (error) {
            console.error("Print Revenue Error:", error);
            Swal.fire('Error', 'Failed to print revenue', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadPdf = async (transactionId) => {
        try {
            setLoading(true);
            const rawId = isNaN(transactionId) ? (revenueData.find(item => item.id === transactionId)?.Id || transactionId) : transactionId;
            const payload = {
                Token: "-2295521862261168",
                Details: { RevenueId: parseInt(rawId, 10) }
            };

            const response = await wcfInstance.post(`${NEWV3_BASE_URL}${API_ENDPOINTS.REVENUE.DOWNLOAD_REVENUE_PDF_CONVERTER}`, payload, {
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Revenue_${rawId}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Download PDF Error:", error);
            Swal.fire('Error', 'Failed to download PDF', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleAction = (type, recordId) => {
        // Use revenueDetails if it matches the recordId, otherwise fallback to list data
        const useDetails = revenueDetails && (
            revenueDetails.Id === recordId || 
            revenueDetails.TransactionId === recordId || 
            revenueDetails.id === recordId
        );
        const record = useDetails ? revenueDetails : revenueData.find(item => item.id === recordId);
        
        if (!record) return;

        if (type === 'mark-sent') {
            handleMarkAsSent(recordId);
            return;
        }

        if (type === 'print-revenue') {
            handlePrintRevenue(recordId);
            return;
        }

        if (type === 'download-pdf') {
            handleDownloadPdf(recordId);
            return;
        }

        const email = record.ContactEmail || record.contact_email || record.Email || "";
        const phone = record.ContactPhone || record.contact_phone || record.Phone || record.Mobile || "";
        const leadId = record.customer_id || record.CustomerId || record.LeadNo || record.LeadId || "";

        setLeadAction({
            open: true,
            mode: type,
            leadId: leadId,
            entity: { ...record, email, phone, CustomerName: record.PersonName || record.CustomerName || record.customerName }
        });
    };

    const handleMassMarkAsSent = async () => {
        const selectedIds = Object.keys(rowSelection);
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: `Do you want to mark ${selectedIds.length} revenue records as sent?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#0366d6',
            cancelButtonColor: '#6a737d',
            confirmButtonText: 'Yes, Mark all as Sent',
            cancelButtonText: 'Cancel'
        });

        if (!result.isConfirmed) return;

        setLoading(true);
        try {
            const transactionIds = selectedIds.map(id => {
                const record = revenueData.find(item => item.id === id);
                return record ? (record.Id || record.id || record.TransactionId || record.RevenueId || record.AutoId) : id;
            }).join(',');

            const payload = {
                Token: "-2295521862261168",
                Details: {
                    Mode: "markassent",
                    RevenueIds: transactionIds,
                    TransactionIds: transactionIds,
                    UserId: parseInt(userId, 10)
                }
            };

            const resp = await wcfInstance.post(Update_Revenue_Status, payload);

            if (resp.data && resp.data.Status === 1) {
                Swal.fire({
                    title: 'Success!',
                    text: `${selectedIds.length} records marked as sent`,
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false
                });
                setRowSelection({});
                fetchRevenue();
            } else {
                Swal.fire('Error', resp.data?.Message || 'Failed to update priority', 'error');
            }
        } catch (error) {
            console.error("Error bulk marking as sent:", error);
            Swal.fire('Error', 'Failed to update status', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleMassDelete = async () => {
        const selectedIds = Object.keys(rowSelection);
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: `Do you want to delete ${selectedIds.length} revenue records? You won't be able to revert this!`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete them!'
        });

        if (!result.isConfirmed) return;

        setLoading(true);
        try {
            const transactionIds = selectedIds.map(id => {
                const record = revenueData.find(item => item.id === id);
                return record ? (record.Id || record.id || record.TransactionId || record.RevenueId || record.AutoId) : id;
            }).join(',');

            const payload = {
                Token: "-2295521862261168",
                Details: {
                    Mode: "deleted",
                    RevenueIds: transactionIds,
                    TransactionIds: transactionIds,
                    UserId: parseInt(userId, 10)
                }
            };

            const resp = await wcfInstance.post(Update_Revenue_Status, payload);

            if (resp.data && resp.data.Status === 1) {
                Swal.fire('Deleted!', `${selectedIds.length} records have been deleted.`, 'success');
                setRowSelection({});
                fetchRevenue();
            } else {
                Swal.fire('Error', resp.data?.Message || 'Failed to delete records', 'error');
            }
        } catch (error) {
            console.error("Error bulk deleting:", error);
            Swal.fire('Error', 'An error occurred while deleting', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        // Find the actual record to get its raw ID if the passed 'id' is not a number
        let targetId = id;
        if (isNaN(id)) {
            const record = revenueData.find(item => item.id === id);
            if (record) {
                targetId = record.Id || record.id || record.TransactionId || record.TransactionID || record.RevenueId || record.RevenueID || record.AutoId || record.AutoID;
            }
        }

        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                const payload = {
                    Token: "-2295521862261168",
                    Details: {
                        Mode: "deleted",
                        RevenueIds: targetId.toString(),
                        TransactionIds: targetId.toString(),
                        UserId: parseInt(userId, 10)
                    }
                };
                const resp = await wcfInstance.post(Update_Revenue_Status, payload);
                if (resp.data && resp.data.Status === 1) {
                    Swal.fire('Deleted!', 'Revenue record has been deleted.', 'success');
                    fetchRevenue();
                } else {
                    Swal.fire('Error', resp.data?.Message || 'Failed to delete record', 'error');
                }
            } catch (error) {
                console.error("Error deleting revenue:", error);
                Swal.fire('Error', 'An error occurred while deleting', 'error');
            }
        }
    };

    // Close action menus on outside click
    useEffect(() => {
        const handleClickOutside = () => setActiveActionMenu(null);
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    // Scorecard Data
    const statsData = useMemo(() => {
        const totalAmount = filteredData.reduce((sum, item) => sum + (parseFloat(item.RevenueAmount || item.Amount) || 0), 0);

        return [
            {
                title: formatCurrency(totalAmount, 'INR'),
                subtitle: 'Total Revenue',
                icon: 'ri:money-dollar-circle-line',
                color: 'primary',
                key: 'All'
            },
            {
                title: filteredData.length,
                subtitle: 'Revenue',
                icon: 'ri:exchange-line',
                color: 'success',
                key: 'Paid'
            },
            {
                title: '₹ 0.00',
                subtitle: 'Pending',
                icon: 'ri:time-line',
                color: 'warning',
                key: 'Pending'
            }
        ];
    }, [filteredData]);

    const columns = useMemo(() => [
        {
            id: 'select',
            header: ({ table }) => (
                <div className="rev-checkbox-wrapper">
                    <input
                        type="checkbox"
                        checked={table.getIsAllRowsSelected()}
                        onChange={table.getToggleAllRowsSelectedHandler()}
                        ref={(input) => {
                            if (input) {
                                input.indeterminate = table.getIsSomeRowsSelected();
                            }
                        }}
                        className="rev-table-checkbox"
                    />
                </div>
            ),
            cell: ({ row }) => (
                <div className="rev-checkbox-wrapper">
                    <input
                        type="checkbox"
                        checked={row.getIsSelected()}
                        onChange={row.getToggleSelectedHandler()}
                        className="rev-table-checkbox"
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            )
        },
        {
            header: 'Customer',
            accessorKey: 'CustomerName',
            cell: ({ row }) => {
                const item = row.original;
                const name = item.CustomerName || item.customerName || item.PersonName || item.personName || item.EntityName || "";
                const phone1 = item.MobileNo || item.mobileno || item.Phone || "";
                const email = item.ContactEmail || item.EmailId || item.emailid || item.Email || "";
                const leadNo = item.LeadNo || item.leadno || "";

                const displayName = name || phone1 || email || "Unknown Customer";

                return (
                    <div className="rev-customer-cell">
                        <ScoreCard
                            leadId={leadNo}
                            mobileNo={phone1}
                            personName={displayName === phone1 ? '' : displayName}
                            initialScore={item.CurrentScore}
                        >

                            <div className="rev-avatar-wrapper">
                                <div
                                    className="rev-avatar"
                                    style={{
                                        backgroundColor: generateColor(displayName).replace('60%)', '95%)'),
                                        color: generateColor(displayName).replace('60%)', '40%)'),
                                        borderColor: generateColor(displayName).replace('60%)', '80%)')
                                    }}
                                >
                                    {getInitials(displayName)}
                                </div>
                            </div>

                        </ScoreCard>
                        <div className="rev-customer-info">
                            <span 
                                className="rev-name" 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedLeadId(leadNo);
                                    setIsLeadDetailOpen(true);
                                }}
                            >
                                {displayName}
                            </span>
                            <div className="rev-meta">
                                {leadNo && <span>Lead No.{leadNo}</span>}
                            </div>
                        </div>
                    </div>
                );
            }
        },
        {
            header: 'Mobile',
            accessorKey: 'MobileNo',
            enableSorting: false,
            cell: ({ row }) => {
                const item = row.original;
                const phone = item.MobileNo || item.mobileno || item.Phone || item.ContactPhone || item.PersonPhone || item.PersonMobile || item.ContactMobile || "";
                const isValid = phone && phone !== "0" && phone !== 0 && String(phone).trim() !== "";
                
                if (!isValid) return <span className="text-muted">--</span>;
                const phoneStr = String(phone).trim();
                return <span className="ph-date-text">{phoneStr.startsWith('+') ? phoneStr : `+91 ${phoneStr}`}</span>;
            }
        },
        {
            header: 'Email',
            accessorKey: 'ContactEmail',
            enableSorting: false,
            cell: ({ row }) => {
                const item = row.original;
                const email = item.ContactEmail || item.EmailId || item.emailid || item.Email || item.PersonEmail || "";
                const isValid = email && email !== "0" && email !== 0 && String(email).trim() !== "";

                if (!isValid) return <span className="text-muted">--</span>;
                return <span className="ph-date-text" style={{ color: '#4b5563' }}>{String(email).trim()}</span>;
            }
        },
        {
            header: 'Date',
            accessorKey: 'sortableDate',
            cell: ({ row }) => {
                const val = row.original.RevenueDate;
                if (!val) return 'N/A';
                if (typeof val === 'string' && val.includes('/Date(')) {
                    const ts = parseInt(val.match(/\d+/)[0], 10);
                    const d = new Date(ts);
                    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-');
                }
                return val;
            }
        },
        {
            header: 'Amount',
            accessorKey: 'RevenueAmount',
            cell: ({ row }) => <span className="rev-amount">{formatCurrency(row.original.RevenueAmount || row.original.revenueamount, 'INR')}</span>
        },
        {
            header: 'Payment Mode',
            accessorKey: 'PaymentMode',
            enableSorting: false,
            cell: ({ row }) => {
                const mode = row.original.PaymentMethod || row.original.PaymentMode || row.original.paymentmode || 'N/A';
                return <span className={`rev-payment-badge ${mode.toLowerCase().replace(/\s+/g, '-')}`}>{mode}</span>;
            }
        },
        {
            header: 'Reference',
            accessorKey: 'ReferenceNo',
            enableSorting: false,
            cell: ({ row }) => <span>{row.original.Reference || row.original.ReferenceNo || row.original.referenceno || '-'}</span>
        },
        {
            header: 'Action',
            id: 'actions',
            cell: ({ row }) => {
                const id = row.original.id;
                const isMenuOpen = activeActionMenu === id;

                return (
                    <div className="rev-actions-container relative">
                        <button
                            className={`rev-action-btn ${isMenuOpen ? 'active' : ''}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                setActiveActionMenu(isMenuOpen ? null : id);
                            }}
                        >
                            <MoreVertical size={16} />
                        </button>
                        {isMenuOpen && (
                            <div className="rev-actions-dropdown" onClick={(e) => e.stopPropagation()}>
                                {crmOperationPerms?.View !== false && (
                                    <div className="rev-dropdown-item" onClick={() => { handleShow(id); setActiveActionMenu(null); }}>
                                        <Eye size={14} className="text-blue-500" /> <span>Show</span>
                                    </div>
                                )}
                                {crmOperationPerms?.Delete !== false && (
                                    <div className="rev-dropdown-item delete" onClick={() => { handleDelete(id); setActiveActionMenu(null); }}>
                                        <Trash2 size={14} className="text-red-500" /> <span>Remove</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );
            }
        }
    ], [activeActionMenu]);


    return (
        <div className="revenue-modern-layout flex flex-col h-screen overflow-hidden">
            {loading && !revenueData.length && <Loader message="Fetching revenue data..." />}

            {/* Standardized Header */}
            <SubHeader
                pageName="Revenue"
                pageCode="PAG10224"
                title="Revenue"
                customPadding="0 20px"
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                searchPlaceholder="Search revenue..."
                viewMode={viewMode === 'list' ? 'table' : viewMode}
                setViewMode={(mode) => setViewMode(mode === 'table' ? 'list' : mode)}
                showViewSwitcher={true}
                showGrid={false}
                showTable={true}
                showCalendar={true}
                showSearch={!!crmOperationPerms?.Search}
                onAddClick={crmOperationPerms?.Add !== false ? () => setShowAddModal(true) : null}
                totalCount={filteredData.length}
                selectedCount={Object.keys(rowSelection).length}
                onClearSelection={() => setRowSelection({})}
            >
                {Object.keys(rowSelection).length > 0 && (
                    <div className="absolute inset-0 z-[100] bg-white flex items-center px-4">
                        <RevenueMassActionPanel
                            selectedCount={Object.keys(rowSelection).length}
                            totalCount={filteredData.length}
                            onClose={() => setRowSelection({})}
                            onGo={(actionId) => {
                                if (actionId === 'delete') {
                                    handleMassDelete();
                                } else if (actionId === 'mark-sent') {
                                    handleMassMarkAsSent();
                                } else if (actionId !== 'entity_action') {
                                    setMassOperationMode(actionId);
                                    setIsMassDrawerOpen(true);
                                }
                            }}
                        />
                    </div>
                )}
            </SubHeader>

            <PremiumModuleWrapper
                moduleName="Revenue"
                loading={loading}
                onStatusClick={setActiveScorecard}
                customCards={statsData}
                data={filteredData}
                columns={columns}
                totalRecords={totalCount}
                currentPage={pageIndex}
                itemsPerPage={pageSize}
                onPageChange={setPageIndex}
                showCards={true}
                showTable={viewMode === 'list'}
                rowSelection={rowSelection}
                onRowSelectionChange={setRowSelection}
                onManualRowSelectionChange={setRowSelection}
            >
                {viewMode === 'calendar' && (
                    <div className="rev-calendar-container scrollbar-thin overflow-auto" style={{ flex: 1, background: '#fff', padding: '0 20px 20px 20px' }}>
                        <DynamicCalendar
                            events={calendarEvents}
                            initialView="month"
                            initialDate={calendarInitialDate}
                            entityLabel="revenue"
                            onEventClick={(event) => {
                                handleShow(event.id);
                            }}
                            eventRenderer={(event) => {
                                const rev = event.extendedProps;
                                const avatarTheme = generateColor(rev.CustomerName || 'Revenue');
                                
                                return (
                                    <div className="rev-cal-event">
                                        <div className="rev-cal-header">
                                            <span className="rev-cal-amount">{formatCurrency(rev.RevenueAmount || rev.Amount, 'INR')}</span>
                                            <span className={`rev-cal-mode ${String(rev.PaymentMode || rev.PaymentMethod || '').toLowerCase().replace(/\s+/g, '-')}`}>
                                                {rev.PaymentMode || rev.PaymentMethod || 'N/A'}
                                            </span>
                                        </div>
                                        <div className="rev-cal-body">
                                            <div className="rev-cal-customer">
                                                <div 
                                                    className="rev-cal-avatar"
                                                    style={{
                                                        backgroundColor: avatarTheme.replace('60%)', '95%)'),
                                                        color: avatarTheme.replace('60%)', '40%)')
                                                    }}
                                                >
                                                    {getInitials(rev.CustomerName || 'Revenue')}
                                                </div>
                                                <span className="rev-cal-name">{rev.CustomerName || 'Unknown'}</span>
                                            </div>
                                            {rev.Reference && <div className="rev-cal-ref">Ref: {rev.Reference}</div>}
                                        </div>
                                    </div>
                                );
                            }}
                        />
                    </div>
                )}
            </PremiumModuleWrapper>

            <Panels
                isOpen={showAddModal}
                onClose={() => setShowAddModal(false)}
                title="Revenue List"
                width="50%"
                footer={(
                    <>
                        <button className="cancel-modal-btn" onClick={() => setShowAddModal(false)} disabled={isSubmitting}>Cancel</button>
                        <button className="submit-rev-btn" type="submit" form="add-revenue-form" disabled={isSubmitting}>
                            {isSubmitting ? 'Saving...' : 'Submit'}
                        </button>
                    </>
                )}
            >
                <AddRevenue
                    onClose={() => setShowAddModal(false)}
                    userId={userId}
                    token="-2295521862261168"
                    suggestionApi={Get_Lead_Customer_Suggestion}
                    currencyApi={Get_Currency_List}
                    invoiceApi={Get_Invoice_Suggestion}
                    saveApi={SAVE_REVENUE}
                    holdingTaxApi={Get_Holding_Tax}
                    onSave={fetchRevenue}
                    setIsSubmitting={setIsSubmitting}
                />
            </Panels>
            <Panels
                isOpen={!!viewingRevenueId}
                onClose={() => {
                    setViewingRevenueId(null);
                    setRevenueDetails(null);
                }}
                title="Revenues"
                width="50%"
                actions={revenueDetails && <RevenueOperations data={revenueDetails} onAction={(type) => handleAction(type, viewingRevenueId)} />}
            >
                {isDetailsLoading ? (
                    <div className="rev-detail-loading-state">
                        <Loader message="Loading details..." />
                    </div>
                ) : (
                    <RevenueDetailView
                        data={revenueDetails}
                        onBack={() => {
                            setViewingRevenueId(null);
                            setRevenueDetails(null);
                        }}
                        onAction={(type) => handleAction(type, viewingRevenueId)}
                    />
                )}
            </Panels>

            {/* Lead Action Modal */}
            {leadAction.open && (
                <div className="lead-action-modal-overlay" onClick={() => setLeadAction({ ...leadAction, open: false })}>
                    <div className="lead-action-modal-content" onClick={e => e.stopPropagation()}>
                        <LeadActionOperation
                            leadId={leadAction.leadId}
                            entity={leadAction.entity}
                            mode={leadAction.mode}
                            onBack={() => setLeadAction({ ...leadAction, open: false })}
                            onSuccess={() => {
                                setLeadAction({ ...leadAction, open: false });
                                fetchRevenue();
                            }}
                        />
                    </div>
                </div>
            )}

            {/* Lead Details Panel */}
            <Panels
                isOpen={isLeadDetailOpen}
                onClose={() => {
                    setIsLeadDetailOpen(false);
                    setSelectedLeadId(null);
                }}
                title="Lead Details"
                width="60%"
            >
                {selectedLeadId && (
                    <LeadDetails
                        leadId={selectedLeadId}
                        onBack={() => {
                            setIsLeadDetailOpen(false);
                            setSelectedLeadId(null);
                        }}
                        module="revenue"
                    />
                )}
            </Panels>

            {/* Mass Operation Drawer */}
            <MassOperationDrawer
                isOpen={isMassDrawerOpen}
                onClose={() => setIsMassDrawerOpen(false)}
                operationMode={massOperationMode}
                selectedCount={Object.keys(rowSelection).length}
                associatedTo="Revenue"
            />
        </div>
    );
};

// Internal Add Revenue Component
const AddRevenue = ({ onClose, userId, token, suggestionApi, currencyApi, invoiceApi, saveApi, onSave, setIsSubmitting, holdingTaxApi }) => {
    const [formData, setFormData] = useState({
        date: new Date().toISOString().split('T')[0],
        currency: 'Rupees (\u20B9)',
        currencyId: '1',
        amount: '0',
        account: 'Sales',
        accountId: '1',
        customerId: '',
        customerName: '',
        description: '',
        paymentMethod: 'Cheque',
        reference: ''
    });
    const [isDateFocused, setIsDateFocused] = useState(false);

    const [customerInput, setCustomerInput] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const searchTimeout = useRef(null);
    const [showPaymentOptions, setShowPaymentOptions] = useState(false);
    const paymentMethods = ['Cheque', 'Cash', '22', 'giftcard'];
    const [attachments, setAttachments] = useState([]);
    
    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        setAttachments(prev => [...prev, ...files]);
    };

    const handleRemoveAttachment = (index) => {
        setAttachments(attachments.filter((_, i) => i !== index));
    };

    // New states for Invoices, Currencies, and Holding Tax
    const [currencies, setCurrencies] = useState([]);
    const [showCurrencyOptions, setShowCurrencyOptions] = useState(false);
    const [addedInvoices, setAddedInvoices] = useState([]);
    const [invoiceSearchText, setInvoiceSearchText] = useState('');
    const [invoiceSuggestions, setInvoiceSuggestions] = useState([]);
    const [showInvoiceSuggestions, setShowInvoiceSuggestions] = useState(false);
    const [isSearchingInvoices, setIsSearchingInvoices] = useState(false);
    const [showInvoiceSearchBox, setShowInvoiceSearchBox] = useState(false);
    const [holdingTaxList, setHoldingTaxList] = useState([]);

    const currentSymbol = useMemo(() => {
        const selected = currencies.find(c => String(c.Id) === String(formData.currencyId));
        if (selected) return selected.Symbol;
        const match = formData.currency.match(/\((.*)\)/);
        return match ? match[1] : '₹';
    }, [currencies, formData.currencyId, formData.currency]);

    // Calculate Advance and Total Adjusted Amount
    const totals = useMemo(() => {
        const totalPaid = parseFloat(formData.amount) || 0;
        const sumAdjusted = addedInvoices.reduce((sum, inv) => sum + (parseFloat(inv.enterAmount) || 0), 0);
        const advance = Math.max(0, totalPaid - sumAdjusted);
        const isRecheckNeeded = sumAdjusted > totalPaid;
        return { totalPaid, sumAdjusted, advance, isRecheckNeeded };
    }, [formData.amount, addedInvoices]);

    useEffect(() => {
        fetchCurrencies();
        fetchHoldingTax();
    }, []);

    const fetchCurrencies = async () => {
        try {
            const response = await wcfInstance.post(currencyApi, { Token: "-2295521862261168", Details: "" });
            if (response.data) {
                const list = response.data.Details || response.data.details || [];
                const arr = Array.isArray(list) ? list : [];
                setCurrencies(arr);
                if (arr.length > 0) {
                    const defaultCurr = arr.find(c => c.Name && (c.Name.toLowerCase().includes('rupee') || c.Name.includes('INR'))) || arr[0];
                    setFormData(prev => ({
                        ...prev,
                        currencyId: defaultCurr.Id,
                        currency: `${defaultCurr.Name} (${defaultCurr.Symbol})`
                    }));
                }
            }
        } catch (error) {
            console.error('Error fetching currencies:', error);
        }
    };

    const fetchHoldingTax = async () => {
        try {
            const payload = {
                Token: token,
                Details: { 
                    UserId: parseInt(userId, 10),
                    FilterText: "",
                    OffsetValue: 0,
                    PagingSize: 500
                }
            };
            const response = await wcfInstance.post(holdingTaxApi, payload);
            if (response.data && response.data.Status === 1) {
                let list = response.data.Details || [];
                if (typeof list === 'string') list = JSON.parse(list);
                setHoldingTaxList(Array.isArray(list) ? list : (list.Table || []));
            }
        } catch (error) {
            console.error('Error fetching holding tax:', error);
        }
    };

    const fetchInvoiceSuggestions = async (searchText, force = false) => {
        if (!force && (!searchText || searchText.length < 1) || !formData.customerId) {
            setInvoiceSuggestions([]);
            return;
        }
        setIsSearchingInvoices(true);
        try {
            const payload = {
                Token: token,
                Details: {
                    LeadId: parseInt(formData.customerId, 10),
                    FilterText: searchText || ""
                }
            };
            const response = await wcfInstance.post(invoiceApi, payload);
            if (response.data && response.data.Status === 1) {
                const details = response.data.Details || [];
                const results = Array.isArray(details) ? details : (details?.Table || details?.Items || []);
                setInvoiceSuggestions(results);
                setShowInvoiceSuggestions(true);
            }
        } catch (error) {
            console.error('Error fetching invoice suggestions:', error);
        } finally {
            setIsSearchingInvoices(false);
        }
    };

    const handleInvoiceSearchChange = (e) => {
        const val = e.target.value;
        setInvoiceSearchText(val);
        if (searchTimeout.current) clearTimeout(searchTimeout.current);
        searchTimeout.current = setTimeout(() => fetchInvoiceSuggestions(val), 500);
    };

    const handleSelectInvoice = (inv) => {
        if (addedInvoices.some(existing => existing.id === inv.InvoiceId)) {
            Swal.fire({
                title: 'Duplicate Invoice',
                text: 'This invoice is already added.',
                icon: 'info',
                confirmButtonColor: '#475569'
            });
            return;
        }

        const newRow = {
            id: inv.InvoiceId,
            number: inv.InvoiceNumber,
            date: inv.InvoiceDate || '',
            totalAmount: inv.InvoiceTotal || 0,
            paidTill: inv.PaidAmount || 0,
            unpaidAmount: inv.InvoiceDueAmount || 0,
            taxId: '0',
            taxRate: 0,
            taxName: '',
            taxAmount: 0,
            dueAmount: inv.InvoiceDueAmount || 0,
            enterAmount: inv.InvoiceDueAmount || 0
        };
        setAddedInvoices([...addedInvoices, newRow]);
        setShowInvoiceSearchBox(false);
        setInvoiceSearchText('');
        setInvoiceSuggestions([]);
    };

    const handleUpdateInvoiceRow = (index, field, value) => {
        const updated = [...addedInvoices];
        const row = updated[index];
        
        if (field === 'taxId') {
            const tax = holdingTaxList.find(t => String(t.Id) === String(value));
            row.taxId = value;
            if (tax) {
                row.taxName = tax.Name;
                row.taxRate = parseFloat(tax.Rate) || 0;
                row.taxAmount = (parseFloat(row.totalAmount) * row.taxRate) / 100;
            } else {
                row.taxName = '';
                row.taxRate = 0;
                row.taxAmount = 0;
            }
            row.dueAmount = parseFloat(row.totalAmount) - (parseFloat(row.paidTill) + row.taxAmount);
            row.enterAmount = row.dueAmount;
        } else {
            row[field] = value;
            if (field === 'totalAmount' || field === 'paidTill') {
                row.dueAmount = parseFloat(row.totalAmount) - (parseFloat(row.paidTill) + row.taxAmount);
                row.enterAmount = row.dueAmount;
            }
        }
        
        setAddedInvoices(updated);
    };

    const handleRemoveInvoiceRow = (index) => {
        setAddedInvoices(addedInvoices.filter((_, i) => i !== index));
    };

    const fetchSuggestions = async (searchText, force = false) => {
        if (!force && (!searchText || searchText.length < 2)) {
            setSuggestions([]);
            return;
        }
        setIsSearching(true);
        try {
            const payload = {
                Token: token,
                Details: { 
                    UserId: parseInt(userId, 10), 
                    FilterText: searchText || "", 
                    SearchText: searchText || "" 
                }
            };
            const response = await wcfInstance.post(suggestionApi, payload);
            if (response.data && response.data.Status === 1) {
                let details = response.data.Details || [];
                if (Array.isArray(details)) {
                    setSuggestions(details);
                    setShowSuggestions(true);
                } else {
                    setSuggestions([]);
                }
            }
        } catch (error) {
            console.error('Error fetching suggestions:', error);
        } finally {
            setIsSearching(false);
        }
    };

    const handleShowAllCustomers = () => {
        fetchSuggestions("", true);
    };

    const handleShowAllInvoices = () => {
        if (formData.customerId) {
            setShowInvoiceSearchBox(true);
            fetchInvoiceSuggestions("", true);
        } else {
            Swal.fire({
                title: 'Customer Required',
                text: 'Please select a customer first.',
                icon: 'info',
                confirmButtonColor: '#475569'
            });
        }
    };

    const handleCustomerInputChange = (e) => {
        const val = e.target.value;
        setCustomerInput(val);
        if (searchTimeout.current) clearTimeout(searchTimeout.current);
        searchTimeout.current = setTimeout(() => fetchSuggestions(val), 500);
    };

    const handleSelectSuggestion = (cust) => {
        const selectedName = cust.PersonName || cust.EntityName || cust.CustomerName || cust.Text || '';
        const selectedId = cust.LeadId || cust.AutoId || cust.Value || cust.Id || '';
        setCustomerInput(selectedName);
        setFormData({ ...formData, customerId: selectedId });
        setShowSuggestions(false);
    };

    const handleSubmit = async () => {
        if (!formData.customerId) {
            Swal.fire({ title: 'Required Field', text: 'Please select a customer before saving.', icon: 'warning', confirmButtonColor: '#475569' });
            return;
        }

        const paidAmt = parseFloat(formData.amount);
        if (isNaN(paidAmt) || paidAmt <= 0) {
            Swal.fire({ title: 'Required Field', text: 'Please enter a valid paid amount.', icon: 'warning', confirmButtonColor: '#475569' });
            return;
        }

        if (totals.isRecheckNeeded) {
            Swal.fire({ title: 'Validation Error', text: 'Total adjusted amount in invoices exceeds the paid amount. Please recheck.', icon: 'error', confirmButtonColor: '#ef4444' });
            return;
        }

        for (const inv of addedInvoices) {
            const adj = parseFloat(inv.enterAmount) || 0;
            const due = parseFloat(inv.dueAmount) || 0;
            if (adj > due) {
                Swal.fire({ title: 'Validation Error', text: `Adjusted amount for invoice ${inv.number} must be less than or equal to due amount.`, icon: 'error', confirmButtonColor: '#ef4444' });
                return;
            }
        }

        setIsSubmitting(true);
        try {
            const formDataPayload = new FormData();
            formDataPayload.append('Token', token);
            formDataPayload.append('UserId', userId);

            const transactionItems = addedInvoices.map(inv => ({
                InvoiceId: String(inv.id || 0),
                TaxId: String(inv.taxId || 0),
                TaxRate: String(inv.taxRate || 0),
                TaxName: inv.taxName || "",
                TaxAmount: parseFloat(inv.taxAmount) || 0,
                Amount: String(inv.enterAmount || 0),
                type: "invoice"
            }));

            // Added Advance item to match legacy logic and ensure SP processes correctly
            if (totals.advance > 0) {
                transactionItems.push({
                    InvoiceId: 0,
                    TaxId: 0,
                    TaxRate: 0,
                    TaxName: "",
                    TaxAmount: 0,
                    Amount: String(totals.advance.toFixed(2)),
                    type: "advance"
                });
            }

            const revenueData = {
                RevenueId: 0,
                RevenueDate: formData.date,
                Amount: String(paidAmt),
                AccountId: String(formData.accountId || 0),
                LeadId: String(formData.customerId || 0),
                CurrencyId: String(formData.currencyId || 0),
                PaymentMothod: formData.paymentMethod || "",
                Reference: formData.reference || "",
                Description: formData.description || "",
                TransactionItems: transactionItems
            };

            formDataPayload.append('RevenueData', JSON.stringify(revenueData));

            if (attachments && attachments.length > 0) {
                attachments.forEach((file) => {
                    formDataPayload.append('file', file);
                });
            }

            const response = await wcfInstance.post(saveApi, formDataPayload, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            const respData = response.data;
            if (respData && (respData.Status === 1 || respData.status === true || respData.Message === "Success" || (typeof respData.Details === 'string' && respData.Details.includes("successfully")))) {
                Swal.fire({ title: 'Saved!', text: 'Revenue has been recorded successfully.', icon: 'success', confirmButtonColor: '#10b981' });
                onSave();
                onClose();
            } else {
                Swal.fire({ title: 'Save Failed', text: respData.Message || 'There was an error saving the revenue record.', icon: 'error', confirmButtonColor: '#ef4444' });
            }
        } catch (error) {
            console.error('Save Error:', error);
            Swal.fire({ title: 'Error', text: 'An unexpected error occurred. Please try again.', icon: 'error', confirmButtonColor: '#ef4444' });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <form id="add-revenue-form" className="add-revenue-form-container" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
            <div className="add-rev-body-standalone">
                <h3 className="add-rev-title">New Revenue</h3>
                <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                    <div className="form-group">
                        <label>Date</label>
                        <div className="input-with-icon">
                            <div className="input-icon-box"><Calendar size={18} /></div>
                            <input
                                type={isDateFocused ? "date" : "text"}
                                value={isDateFocused ? formData.date : formatDateForDisplay(formData.date)}
                                onFocus={() => setIsDateFocused(true)}
                                onBlur={() => setIsDateFocused(false)}
                                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                            />
                        </div>
                    </div>
                    <div className="form-group">
                        <label>Currency</label>
                        <div className="input-with-icon premium-select-wrapper" onClick={() => setShowCurrencyOptions(!showCurrencyOptions)}>
                            <div className="input-icon-box" style={{ fontSize: '16px', fontWeight: 'bold', color: '#64748b' }}>{currentSymbol}</div>
                            <div className="premium-select-trigger">
                                <span>{formData.currency}</span>
                                <ChevronDown size={16} />
                            </div>
                            {showCurrencyOptions && (
                                <div className="premium-select-options scrollable">
                                    {currencies.map(curr => {
                                        const label = `${curr.Name} (${curr.Symbol})`;
                                        const isSelected = String(formData.currencyId) === String(curr.Id);
                                        return (
                                            <div
                                                key={curr.Id}
                                                className={`option-item ${isSelected ? 'selected' : ''}`}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setFormData({ ...formData, currency: label, currencyId: curr.Id });
                                                    setShowCurrencyOptions(false);
                                                }}
                                            >
                                                <div className="option-checkbox">
                                                    <div className={`checkbox-box ${isSelected ? 'checked' : ''}`}>
                                                        {isSelected && <Check size={10} />}
                                                    </div>
                                                </div>
                                                <span className="option-label">{label}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="form-group">
                        <label>Paid Amount</label>
                        <div className="input-with-icon">
                            <div className="input-icon-box" style={{ fontSize: '16px', fontWeight: 'bold', color: '#64748b' }}>{currentSymbol}</div>
                            <input type="number" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} />
                        </div>
                    </div>
                </div>
                
                <div className="form-grid mt-4">
                    <div className="form-group">
                        <label>Account</label>
                        <div className="input-with-icon">
                            <div className="input-icon-box"><Building2 size={18} /></div>
                            <select value={formData.accountId} onChange={(e) => {
                                const val = e.target.value;
                                const label = val === '1' ? 'Sales' : 'Service';
                                setFormData({ ...formData, accountId: val, account: label });
                            }}>
                                <option value="1">Sales</option>
                                <option value="2">Service</option>
                            </select>
                        </div>
                    </div>
                    <div className="form-group">
                        <div className="label-row">
                            <label>Customer</label>
                        </div>
                        <div className="input-with-icon">
                            <div className="input-icon-box"><User size={18} /></div>
                            <input 
                                type="text" 
                                value={customerInput} 
                                onChange={handleCustomerInputChange} 
                                onFocus={() => suggestions.length > 0 && setShowSuggestions(true)} 
                                placeholder="Type Customer name, mobile, email" 
                            />
                            {showSuggestions && suggestions.length > 0 && (
                                <div className="autocomplete-results">
                                    {suggestions.map((cust, i) => {
                                        const name = cust.PersonName || cust.EntityName || cust.CustomerName || cust.Text;
                                        const phone = cust.MobileNo || cust.Phone || '';
                                        const email = cust.ContactEmail || cust.EmailId || cust.Email || cust.email || '';
                                        const imgPath = cust.ProfileImgFileName || cust.Image || '';
                                        const fullImgUrl = imgPath ? (imgPath.startsWith('http') ? imgPath : `https://kit19.com${imgPath}`) : '';

                                        return (
                                            <div key={i} className="suggestion-item customer-suggestion-item" onClick={() => handleSelectSuggestion(cust)}>
                                                <div className="customer-suggestion-avatar">
                                                    <div className="customer-suggestion-avatar-inner">
                                                        {!isGenericAvatar(fullImgUrl) && fullImgUrl ? (
                                                            <img src={fullImgUrl} alt={name} onError={(e) => { e.target.onerror = null; e.target.src = ''; }} />
                                                        ) : (
                                                            getInitials(name || phone)
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="customer-suggestion-info">
                                                    <div className="customer-suggestion-name-row">
                                                        <span className="customer-suggestion-name">{name || 'Unknown'}</span>
                                                    </div>
                                                    <div className="customer-suggestion-meta">
                                                        {phone && (
                                                            <div className="customer-suggestion-detail">
                                                                <Phone size={12} />
                                                                {phone}
                                                            </div>
                                                        )}
                                                        {email && (
                                                            <div className="customer-suggestion-detail">
                                                                <Mail size={12} />
                                                                {email}
                                                            </div>
                                                        )}
                                                        {cust.CompanyName && (
                                                            <div className="customer-suggestion-detail">
                                                                <Building2 size={12} />
                                                                {cust.CompanyName}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    <div
                                        className="suggestion-add-new"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            console.log("Add New Customer Clicked");
                                            setShowSuggestions(false);
                                        }}
                                    >
                                        <Plus size={16} />
                                        Add New
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="form-group full-width mt-4">
                    <label>Description</label>
                    <div className="input-with-icon">
                        <textarea rows="1" placeholder="Please enter Description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} style={{ paddingLeft: '12px' }} />
                        <div style={{ position: 'absolute', right: '12px', top: '10px' }}><FileText size={18} className="text-gray-300" /></div>
                    </div>
                </div>

                <div className="form-grid mt-4">
                    <div className="form-group">
                        <label>Payment Method</label>
                        <div className="input-with-icon premium-select-wrapper" onClick={() => setShowPaymentOptions(!showPaymentOptions)}>
                            <div className="input-icon-box"><CreditCard size={18} /></div>
                            <div className="premium-select-trigger"><span>{formData.paymentMethod}</span><ChevronDown size={16} /></div>
                            {showPaymentOptions && (
                                <div className="premium-select-options">
                                    {paymentMethods.map(m => (
                                        <div key={m} className={`option-item ${formData.paymentMethod === m ? 'selected' : ''}`} onClick={(e) => { e.stopPropagation(); setFormData({ ...formData, paymentMethod: m }); setShowPaymentOptions(false); }}>
                                            <div className="option-checkbox">{formData.paymentMethod === m && <Check size={12} />}</div>
                                            <span>{m}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="form-group">
                        <label>Reference</label>
                        <div className="input-with-icon">
                            <div className="input-icon-box"><FileText size={18} /></div>
                            <input type="text" placeholder="Enter Reference" value={formData.reference} onChange={(e) => setFormData({ ...formData, reference: e.target.value })} />
                        </div>
                    </div>
                </div>

                <div className="rev-section-header mt-6">
                    <div className="label-row w-full">
                        <h4 className="rev-section-label">Invoice</h4>
                    </div>
                </div>
                <div style={{ marginBottom: '16px' }}>
                    <button className="add-invoice-text-trigger" type="button" onClick={() => setShowInvoiceSearchBox(!showInvoiceSearchBox)}>
                        <Plus size={16} /> Add Invoice
                    </button>
                </div>

            {showInvoiceSearchBox && (
                <div className="invoice-search-container">
                    <div className="input-with-icon">
                        <div className="input-icon-box"><Search size={18} /></div>
                        <input
                            type="text"
                            placeholder="Search Invoices..."
                            value={invoiceSearchText}
                            onChange={handleInvoiceSearchChange}
                            autoFocus
                        />
                        {showInvoiceSuggestions && invoiceSuggestions.length > 0 && (
                            <div className="autocomplete-results">
                                {invoiceSuggestions.map((inv, i) => (
                                    <div key={i} className="suggestion-item customer-suggestion-item" onClick={() => handleSelectInvoice(inv)}>
                                        <div className="customer-suggestion-avatar">
                                            <div className="customer-suggestion-avatar-inner" style={{ backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #dcfce7', borderRadius: '8px', textTransform: 'none' }}>
                                                <FileText size={18} />
                                            </div>
                                        </div>
                                        <div className="customer-suggestion-info">
                                            <div className="customer-suggestion-name-row">
                                                <span className="customer-suggestion-name">{inv.InvoiceNumber}</span>
                                                <span className="customer-suggestion-pill" style={{ background: '#f0f9ff', color: '#0369a1' }}>
                                                    {inv.CurrencySymbol || '₹'} {inv.TotalAmount}
                                                </span>
                                            </div>
                                            <div className="customer-suggestion-meta">
                                                <div className="customer-suggestion-detail">
                                                    <Calendar size={12} />
                                                    Date: {inv.InvoiceDate}
                                                </div>
                                                {inv.InvoiceDueAmount > 0 && (
                                                    <div className="customer-suggestion-detail" style={{ color: '#dc2626' }}>
                                                        Due: {inv.CurrencySymbol || '₹'} {inv.InvoiceDueAmount}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            <div className="rev-invoice-section">
                <div className="rev-table-scroll-wrapper">
                    <table className="rev-static-table rev-invoice-table">
                        <thead>
                            <tr>
                                <th>Invoice Number</th>
                                <th>Invoice Date</th>
                                <th style={{ width: '120px' }}>Total Amount</th>
                                <th style={{ width: '120px' }}>Paid Till</th>
                                <th style={{ width: '150px' }}>Withholding Tax</th>
                                <th style={{ width: '120px' }}>Due Amount</th>
                                <th style={{ width: '120px' }}>Enter Amount</th>
                                <th style={{ width: '40px' }}></th>
                            </tr>
                        </thead>
                    <tbody>
                        {addedInvoices.length === 0 ? (
                            <tr>
                                <td colSpan="8" className="rev-no-data text-center py-4">
                                    <div style={{ background: '#f8f9fa', padding: '12px', borderRadius: '4px' }}>
                                        {customerInput ? `No invoices added. Click + Add Invoice to search.` : 'Select a customer to see invoices'}
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            addedInvoices.map((row, idx) => (
                                <tr key={idx} className="rev-invoice-row">
                                    <td>{row.number}</td>
                                    <td>{row.date}</td>
                                    <td>
                                        <input type="number" className="table-input" value={row.totalAmount} onChange={(e) => handleUpdateInvoiceRow(idx, 'totalAmount', e.target.value)} />
                                    </td>
                                    <td>
                                        <input type="number" className="table-input" value={row.paidTill} onChange={(e) => handleUpdateInvoiceRow(idx, 'paidTill', e.target.value)} />
                                    </td>
                                    <td>
                                        <select 
                                            className="table-input" 
                                            value={row.taxId} 
                                            onChange={(e) => handleUpdateInvoiceRow(idx, 'taxId', e.target.value)}
                                        >
                                            <option value="0">None</option>
                                            {holdingTaxList.map(tax => (
                                                <option key={tax.Id} value={tax.Id}>{tax.Name} ({tax.Rate}%)</option>
                                            ))}
                                        </select>
                                    </td>
                                    <td className="font-medium">{row.dueAmount.toFixed(2)}</td>
                                    <td>
                                        <input 
                                            type="number" 
                                            className={`table-input ${parseFloat(row.enterAmount) > parseFloat(row.dueAmount) ? 'input-error' : ''}`} 
                                            value={row.enterAmount} 
                                            onChange={(e) => handleUpdateInvoiceRow(idx, 'enterAmount', e.target.value)} 
                                        />
                                    </td>
                                    <td>
                                        <button type="button" className="rev-row-remove" onClick={() => handleRemoveInvoiceRow(idx)}>
                                            <Trash2 size={16} />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                    {addedInvoices.length > 0 && (
                        <tfoot>
                            {totals.advance > 0 && (
                                <tr className="advance-row">
                                    <td colSpan="5" className="text-right font-bold pr-4">Advance</td>
                                    <td colSpan="2" className="font-bold text-success">
                                        {currentSymbol} {totals.advance.toFixed(2)}
                                    </td>
                                    <td></td>
                                </tr>
                            )}
                            {totals.isRecheckNeeded && (
                                <tr>
                                    <td colSpan="8" className="text-danger text-center py-2 font-medium bg-red-50">
                                        Warning: Sum of adjusted amounts exceeds the total paid amount.
                                    </td>
                                </tr>
                            )}
                        </tfoot>
                    )}
                </table>
            </div>
        </div>

        <div className="rev-attachment-section mt-6">
            <h4 className="rev-section-label">Attachment</h4>
            <div className="rev-upload-zone">
                <table className="rev-static-table" style={{ marginTop: 0 }}>
                    <thead>
                        <tr>
                            <th>File Name</th>
                            <th>File Type</th>
                            <th>File Size</th>
                            <th style={{ width: '40px' }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {attachments.length === 0 ? (
                            <tr>
                                <td colSpan="4" className="text-center py-6">
                                    <label className="upload-input-label">
                                        <input type="file" className="hidden-input" multiple onChange={handleFileChange} />
                                        <div className="upload-btn-styled">
                                            <Upload size={16} style={{ marginRight: '8px' }} /> Choose files...
                                        </div>
                                    </label>
                                </td>
                            </tr>
                        ) : (
                            <>
                                {attachments.map((file, idx) => (
                                    <tr key={idx}>
                                        <td>{file.name}</td>
                                        <td>{file.type || 'Unknown'}</td>
                                        <td>{(file.size / 1024).toFixed(2)} KB</td>
                                        <td>
                                            <button type="button" className="rev-row-remove" onClick={() => handleRemoveAttachment(idx)}>
                                                <Trash2 size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                <tr>
                                    <td colSpan="4" className="text-center py-2">
                                        <label className="upload-more-label">
                                            <input type="file" className="hidden-input" multiple onChange={handleFileChange} />
                                            <span className="text-primary cursor-pointer hover:underline">+ Add more files</span>
                                        </label>
                                    </td>
                                </tr>
                            </>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    </div>
            {(showSuggestions || showPaymentOptions || showCurrencyOptions || showInvoiceSuggestions) && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => {
                    setShowSuggestions(false);
                    setShowPaymentOptions(false);
                    setShowCurrencyOptions(false);
                    setShowInvoiceSuggestions(false);
                }} />
            )}
        </form>
    );
};


const RevenueOperations = ({ data, onAction }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const shareActions = [
        { id: 'send-mail', label: 'Email', icon: Mail },
        { id: 'send-sms', label: 'SMS', icon: MessageSquare },
        { id: 'send-whatsapp', label: 'Whatsapp', icon: MessageCircle },
    ];

    return (
        <div className="rev-operations-container" ref={dropdownRef}>
            <button className="rev-settings-btn" onClick={() => setIsOpen(!isOpen)}>
                <Settings size={18} />
            </button>
            {isOpen && (
                <div className="rev-operations-dropdown">
                    <div className="dropdown-section">
                        <div className="dropdown-section-header">
                            <Share2 size={14} />
                            <span>Share Invoice</span>
                        </div>
                        <div className="dropdown-sub-items">
                            {shareActions.map(action => (
                                <div key={action.id} className="dropdown-item sub-item" onClick={() => { onAction(action.id); setIsOpen(false); }}>
                                    <action.icon size={14} />
                                    <span>{action.label}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="dropdown-divider"></div>

                    <div className="dropdown-item" onClick={() => { onAction('mark-sent'); setIsOpen(false); }}>
                        <Check size={14} />
                        <span>Mark as Sent</span>
                    </div>

                    <div className="dropdown-item" onClick={() => { onAction('print-revenue'); setIsOpen(false); }}>
                        <Printer size={14} />
                        <span>Print</span>
                    </div>

                    <div className="dropdown-item" onClick={() => { onAction('download-pdf'); setIsOpen(false); }}>
                        <Download size={14} />
                        <span>Download PDF</span>
                    </div>
                </div>
            )}
        </div>
    );
};

const RevenueMassActionPanel = ({ selectedCount, totalCount, onGo, onClose }) => {
    const [selectedAction, setSelectedAction] = useState('');

    return (
        <div className="mass-operation-panel-central header-variant">
            <div className="flex items-center gap-4 relative">
                <div className="selection-badge-premium">
                    <span className="badge-count">{selectedCount}</span>
                    <span className="badge-text uppercase font-bold tracking-wider">Selected of {totalCount}</span>
                </div>

                <div className="flex items-center gap-2">
                    <select
                        value={selectedAction}
                        onChange={(e) => setSelectedAction(e.target.value)}
                        className="mass-action-dropdown-premium min-w-[200px]"
                    >
                        <option value="">Bulk Actions</option>
                        <option value="delete">Delete</option>
                    </select>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => onGo(selectedAction)}
                            disabled={!selectedAction}
                            className="go-button-premium uppercase"
                        >
                            GO
                        </button>
                    </div>
                </div>
            </div>

            <div className="flex items-center">
                <button
                    onClick={onClose}
                    className="p-2 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600 transition-all"
                    title="Clear selection"
                >
                    <X className="w-5 h-5" />
                </button>
            </div>
        </div>
    );
};

const RevenueDetailView = ({ data, onBack, onAction }) => {
    if (!data) return null;

    return (
        <div className="rev-detail-panel-container">
            <div className="rev-detail-content">
                <div className="rev-detail-card-standalone">
                    <div className="rev-detail-section-label">Revenue Received</div>

                    <div className="rev-detail-customer-box">
                        <div className="detail-label">Customer</div>
                        <div className="detail-value highlight">{data.PersonName || 'N/A'}</div>
                        <div className="detail-label mt-2">Amount</div>
                        <div className="detail-value">{formatCurrency(data.amount)}</div>
                    </div>

                    <div className="rev-detail-inner-card">
                        <div className="inner-card-header">
                            <div className="company-info">
                                {data.CompanyLogo ? (
                                    <img src={data.CompanyLogo} alt="Logo" className="company-logo-img" />
                                ) : (
                                    <div className="company-logo-placeholder">{data.CompanyName?.charAt(0) || 'C'}</div>
                                )}
                                <div className="company-text">
                                    <div className="company-name">{data.CompanyName || 'Company Name'}</div>
                                    <div className="company-tax">{data.CompanyTaxNumber || ''}</div>
                                </div>
                            </div>
                        </div>

                        <div className="inner-card-body">
                            <h3 className="inner-card-title">REVENUE RECEIVED</h3>

                            <div className="details-grid">
                                <div className="detail-row">
                                    <span className="row-label">Date:</span>
                                    <span className="row-value underline">{data.paid_at || data.created_at}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="row-label">Payment Method:</span>
                                    <span className="row-value underline">{data.payment_method || 'N/A'}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="row-label">Reference:</span>
                                    <span className="row-value underline">{data.reference || 'N/A'}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="row-label">Description:</span>
                                    <span className="row-value underline">{data.remarks || '-'}</span>
                                </div>
                                <div className="detail-row paid-by">
                                    <span className="row-label">Paid By</span>
                                    <div className="paid-by-info">
                                        <div className="row-value">{data.PersonName}</div>
                                        <div className="row-sub">{data.ContactEmail}</div>
                                    </div>
                                </div>
                            </div>

                            <div className="detail-amount-box">
                                <div className="amount-label">Amount</div>
                                <div className="amount-value">{data.CurrencySymbol || '₹'} {data.amount}</div>
                            </div>
                        </div>

                        {data.revenueItems && data.revenueItems.length > 0 && (
                            <div className="related-invoices-section">
                                <h4 className="section-title">Related Invoice(s)</h4>
                                <div className="details-table-wrapper">
                                    <table className="details-table">
                                        <thead>
                                            <tr>
                                                <th>NUMBER</th>
                                                <th>CUSTOMER</th>
                                                <th>INVOICE DATE</th>
                                                <th>INVOICE AMOUNT</th>
                                                <th>PAID AMOUNT</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.revenueItems.map((item, idx) => (
                                                <tr key={idx}>
                                                    <td className="inv-num">{item.invoice_number || 'Advance'}</td>
                                                    <td>{item.contact_name || data.PersonName || 'N/A'}</td>
                                                    <td>{item.created_at || 'N/A'}</td>
                                                    <td>{data.CurrencySymbol || '₹'} {item.InvoiceAmount || item.invoice_amount || 0}</td>
                                                    <td className="inv-paid">{data.CurrencySymbol || '₹'} {item.amount || 0}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RevenueModule;
